import { Activity } from '../types/recommendations';
import { mapActivitiesResponse } from './goiconActivityMapper';

export interface GoIconActivitiesServiceConfig {
  facilityId: string;
  apiKey: string; // caremerge_api_key from JWT
  residentId?: string; // GoIcon user ID to scope activities to a specific resident
}

export interface GoIconDimensionSummary {
  id: number;
  name: string;
  iconPath?: string;
  color?: string;
}

export interface GoIconFacilityActivitiesQuery {
  i_social?: boolean;
  i_started_date?: number; // unix seconds
  i_ended_date?: number; // unix seconds
  // allow additional passthrough params without losing type safety
  [key: string]: string | number | boolean | undefined;
}

/**
 * GoIcon Activities Service
 * Mirrors the EventsService approach, but for:
 * GET /facilities/{facilityId}/activities?...
 */
export class GoIconActivitiesService {
  private config: GoIconActivitiesServiceConfig;

  constructor(config: GoIconActivitiesServiceConfig) {
    this.config = config;
  }

  private getProxyUrl(): string {
    if (process.env.NODE_ENV === 'development') {
      return 'https://wellness.goicon.co/.netlify/functions/goicon-api-proxy';
    }
    return '/.netlify/functions/goicon-api-proxy';
  }

  async getFacilityActivities(query: GoIconFacilityActivitiesQuery = {}): Promise<Activity[]> {
    const raw = await this.getFacilityActivitiesRaw(query);
    return mapActivitiesResponse(raw);
  }

  /**
   * Fetch only the unique GoIcon dimensions for this facility by having the proxy
   * extract them server-side. Avoids the Lambda 6 MB response-size limit that
   * would be hit when returning the full activities payload.
   */
  async getFacilityDimensionsOnly(query: GoIconFacilityActivitiesQuery = {}): Promise<GoIconDimensionSummary[]> {
    const qs = new URLSearchParams();
    Object.entries(query).forEach(([k, v]) => {
      if (v === undefined) return;
      if (typeof v === 'boolean') qs.set(k, v ? 'true' : 'false');
      else qs.set(k, String(v));
    });

    const endpoint = `/facilities/${this.config.facilityId}/activities${qs.toString() ? `?${qs.toString()}` : ''}`;
    const proxyUrl = this.getProxyUrl();
    const url = `${proxyUrl}?endpoint=${encodeURIComponent(endpoint)}&extractDimensions=true`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-GoIcon-API-Key': this.config.apiKey,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`GoIcon activities API error ${response.status}: ${errorText.slice(0, 300)}`);
    }

    const data = await response.json();
    return (data.dimensions as GoIconDimensionSummary[]) || [];
  }

  /**
   * Fetch raw activities payload from GoIcon.
   * The activities endpoint typically returns an array (not wrapped).
   */
  async getFacilityActivitiesRaw(query: GoIconFacilityActivitiesQuery = {}): Promise<any> {
    const qs = new URLSearchParams();
    Object.entries(query).forEach(([k, v]) => {
      if (v === undefined) return;
      if (typeof v === 'boolean') qs.set(k, v ? 'true' : 'false');
      else qs.set(k, String(v));
    });

    const basePath = this.config.residentId
      ? `/facilities/${this.config.facilityId}/users/${this.config.residentId}/activities`
      : `/facilities/${this.config.facilityId}/activities`;
    const endpoint = `${basePath}${qs.toString() ? `?${qs.toString()}` : ''}`;
    const proxyUrl = this.getProxyUrl();
    const url = `${proxyUrl}?endpoint=${encodeURIComponent(endpoint)}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-GoIcon-API-Key': this.config.apiKey, // Proxy will convert to cookie
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`GoIcon activities API error ${response.status}: ${errorText.slice(0, 300)}`);
    }

    return await response.json();
  }

  /**
   * Fetch the resident's schedule from GoIcon calendar-central.
   * User-scoped via the auth cookie (apiKey) -- returns only the active resident's events.
   * GET /facilities/{facilityId}/social/calendar-central/schedule?start_time=...&end_time=...
   */
  async getResidentSchedule(startTime: number, endTime: number): Promise<any[]> {
    const endpoint = `/facilities/${this.config.facilityId}/social/calendar-central/schedule?start_time=${startTime}&end_time=${endTime}`;
    const proxyUrl = this.getProxyUrl();
    const url = `${proxyUrl}?endpoint=${encodeURIComponent(endpoint)}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-GoIcon-API-Key': this.config.apiKey,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`GoIcon schedule API error ${response.status}: ${errorText.slice(0, 300)}`);
    }

    const data = await response.json();
    console.log('📅 GoIcon schedule response:', Array.isArray(data) ? `${data.length} events` : typeof data, data);
    return Array.isArray(data) ? data : (data?.items || data?.data || data?.events || []);
  }

  /**
   * Extract dimension counts from a schedule response.
   * Tries common field paths: eventDimensions, dimensions, event.dimensions, etc.
   */
  static extractDimensionCountsFromSchedule(
    events: any[],
    goiconToDimId: Record<number, string>
  ): Record<string, number> {
    const dimCounts: Record<string, number> = {};

    for (const evt of events) {
      const eds = evt?.eventDimensions || evt?.dimensions || evt?.event_dimensions || [];
      if (!Array.isArray(eds)) continue;

      for (const ed of eds) {
        const gid = Number(
          ed?.fk_dimension_id ?? ed?.dimension_id ?? ed?.dimension?.id ?? ed?.id
        );
        const wdId = goiconToDimId[gid];
        if (wdId) {
          const pct = Number(ed?.i_percentage ?? 0);
          dimCounts[wdId] = (dimCounts[wdId] || 0) + pct;
        }
      }
    }

    return dimCounts;
  }

  /**
   * Extract unique GoIcon dimensions from a raw activities response.
   * Looks for activity.eventDimensions[].dimension.{id,v_name,v_icon_path,v_color}
   */
  static extractDimensionsFromActivities(raw: any): GoIconDimensionSummary[] {
    const activities = Array.isArray(raw)
      ? raw
      : (raw?.activities || raw?.items || raw?.data || []);

    if (!Array.isArray(activities)) return [];

    const byId = new Map<number, GoIconDimensionSummary>();

    for (const a of activities) {
      const eds = a?.eventDimensions;
      if (!Array.isArray(eds)) continue;

      for (const ed of eds) {
        const dim = ed?.dimension || {};
        const id = Number(dim.id ?? ed?.fk_dimension_id);
        if (!Number.isFinite(id)) continue;

        const nameRaw = dim.v_name ?? dim.name ?? dim.title;
        const name = typeof nameRaw === 'string' ? nameRaw.trim() : '';
        if (!name) continue;

        const iconPath = typeof dim.v_icon_path === 'string' ? dim.v_icon_path : undefined;
        const color = typeof dim.v_color === 'string' ? dim.v_color : undefined;

        if (!byId.has(id)) {
          byId.set(id, { id, name, iconPath, color });
        }
      }
    }

    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
  }
}

